if (!customElements.get('sticky-cta')) {
  class StickyCta extends HTMLElement {
    static SELECTORS = {
      cta: '.sticky-cta',
      header: '#shopify-section-group-header-group, sticky-header, .site-header, header',
      tabsBar: 'linkbar.tabs__link-bar',
      addToCartBar: 'product-sticky-add-to-cart',
      variantInput: 'input[name="id"]',
      variantPrices: 'script[data-variant-prices]',
    };

    static CLASSES = {
      visible: 'is-visible',
    };

    static TRIGGERS = {
      untilScroll: 'until_scroll',
      afterScrollPercent: 'after_scroll_percent',
    };

    static DEFAULTS = {
      scrollThreshold: 200,
      scrollPercent: 0,
      topWithHeader: 195,
      topWithoutHeader: 85,
      topMobileWithHeader: 195,
      topMobileWithoutHeader: 85,
    };

    connectedCallback() {
      this._abort = new AbortController();
      this._cta = this.querySelector(StickyCta.SELECTORS.cta);
      this._threshold = this._toInt(this.dataset.scrollThreshold, StickyCta.DEFAULTS.scrollThreshold);
      this._trigger = this.dataset.trigger || StickyCta.TRIGGERS.untilScroll;
      this._scrollPercent = this._toInt(this.dataset.scrollPercent, StickyCta.DEFAULTS.scrollPercent);
      this._minPrice = this._toInt(this.dataset.minPrice, 0);
      this._variantPrices = this._parseVariantPrices();
      this._header = document.querySelector(StickyCta.SELECTORS.header);
      this._addToCartBar = document.querySelector(StickyCta.SELECTORS.addToCartBar);

      if (!this._cta) return;

      const opts = { signal: this._abort.signal, passive: true };
      window.addEventListener('scroll', () => this._onScroll(), opts);
      this._bindVariantChange();
      this._onScroll();
    }

    disconnectedCallback() {
      this._abort?.abort();
    }

    _bindVariantChange() {
      if (!this._minPrice) return;

      const opts = { signal: this._abort.signal };
      // input[name="id"] is updated by the variant picker when a variant is selected
      const form = document.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      this._variantInput = form.querySelector(StickyCta.SELECTORS.variantInput);
      if (!this._variantInput) return;

      this._variantInput.addEventListener('change', () => this._onScroll(), opts);
    }

    _parseVariantPrices() {
      try {
        const el = this.querySelector(StickyCta.SELECTORS.variantPrices);
        return el ? JSON.parse(el.textContent) : {};
      } catch {
        return {};
      }
    }

    _isPriceEligible() {
      if (!this._minPrice) return true;
      if (!this._variantInput) return true;
      const price = this._variantPrices[this._variantInput.value];
      return price == null || price >= this._minPrice;
    }

    _isScrollEligible() {
      if (this._trigger !== StickyCta.TRIGGERS.afterScrollPercent) {
        return window.scrollY < this._threshold;
      }

      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return true;
      return window.scrollY / scrollable >= this._scrollPercent / 100;
    }

    _onScroll() {
      const visible = this._isScrollEligible() && this._isPriceEligible();
      this._cta.classList.toggle(StickyCta.CLASSES.visible, visible);
      this._cta.setAttribute('aria-hidden', String(!visible));
      this._updateTopPosition();
      this._updateBottomPosition();
    }

    _updateTopPosition() {
      if (!this._header) return;
      const headerVisible = this._header.getBoundingClientRect().bottom > 0;

      this._cta.style.setProperty(
        '--sticky-cta-top',
        headerVisible ? `${StickyCta.DEFAULTS.topWithHeader}px` : `${StickyCta.DEFAULTS.topWithoutHeader}px`
      );

      this._cta.style.setProperty('--sticky-cta-top-mobile', this._getMobileTop(headerVisible));
    }

    _updateBottomPosition() {
      const bar = this._addToCartBar;
      if (!bar) return;
      const rect = bar.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.height > 0;
      const bottom = isVisible ? `${Math.round(rect.height) + 30}px` : '20px';
      this._cta.style.setProperty('--sticky-cta-bottom', bottom);
    }

    _getMobileTop(headerVisible) {
      const tabsBar = document.querySelector(StickyCta.SELECTORS.tabsBar);
      if (tabsBar) {
        const rect = tabsBar.getBoundingClientRect();
        const isStuck = rect.top >= 0 && rect.bottom > 0 && rect.top < 300;
        if (isStuck) return `${Math.round(rect.bottom) + 10}px`;
      }
      return headerVisible
        ? `${StickyCta.DEFAULTS.topMobileWithHeader}px`
        : `${StickyCta.DEFAULTS.topMobileWithoutHeader}px`;
    }

    _toInt(value, fallback) {
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? fallback : parsed;
    }
  }

  customElements.define('sticky-cta', StickyCta);
}
